import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';

// Экран получателя подарка. Показывается ОДИН раз, сразу после активации.
// Задача экрана — не рассказать про функции, а дать человеку заметить,
// что кто-то в него вложился. Функции он найдёт сам.
// Payload кладёт GiftApplier в App.tsx (localStorage), чтобы экран пережил
// онбординг и перезагрузку страницы.

export const GIFT_WELCOME_KEY = 'gift_welcome';

export type GiftWelcomePayload = {
  code: string;
  giver_name: string;
  note: string | null;
  days: number;
  plan: 'month' | 'year';
};

export function readGiftWelcome(): GiftWelcomePayload | null {
  try {
    const raw = localStorage.getItem(GIFT_WELCOME_KEY);
    return raw ? (JSON.parse(raw) as GiftWelcomePayload) : null;
  } catch {
    return null;
  }
}

export default function GiftWelcome() {
  const nav = useNavigate();
  const [payload] = useState<GiftWelcomePayload | null>(() => readGiftWelcome());
  const [tellGiver, setTellGiver] = useState(false);

  useEffect(() => {
    if (!payload) nav('/', { replace: true });
  }, [payload, nav]);

  if (!payload) return null;

  const term = payload.plan === 'year' ? 'год' : '30 дней';

  async function start() {
    localStorage.removeItem(GIFT_WELCOME_KEY);
    if (tellGiver && payload) {
      // Согласие необязательное — сбой тут не должен мешать войти в приложение
      try {
        await supabase.rpc('set_gift_finish_consent', {
          p_code: payload.code,
          p_value: true,
        });
      } catch (e) {
        console.error('gift consent error:', e);
      }
    }
    nav('/', { replace: true });
  }

  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-md mx-auto px-5 pt-10 pb-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 18 }}
          className="text-center mb-7"
        >
          <div className="text-5xl mb-4">🎁</div>
          <h1 className="font-display text-3xl font-bold mb-2">Тебе подарили подписку</h1>
          <p className="text-ink-muted">
            <b className="text-ink">{payload.giver_name}</b> оплатил(а) за тебя {term} в Slava Health.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="card-elevated border-emerald-glow/30 p-5 space-y-4 text-[15px] leading-relaxed text-ink-muted"
        >
          <p>Остановись на секунду. Не на приложении — на том, что за ним стоит.</p>
          <p>
            Кто-то подумал о тебе. Не между делом, не «надо бы как-нибудь». Взял(а) и
            сделал(а). Потратил(а) свои деньги, чтобы тебе стало легче.
          </p>
          <p className="text-ink font-medium">
            Так поступают не со всеми. Так поступают с теми, кто дорог.
          </p>
          <p>
            И вот что тут важно. Если рядом с тобой есть такие люди — значит, ты уже сделал(а)
            в жизни что-то очень правильное. Это не про вес и не про калории. Это про то,
            каким(ой) тебя видят.
          </p>
        </motion.div>

        {payload.note && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="card p-4 mt-4 border-l-2 border-emerald-glow"
          >
            <div className="label mb-2">Сообщение от {payload.giver_name}</div>
            <p className="text-[15px] text-ink italic leading-relaxed whitespace-pre-line">
              {payload.note}
            </p>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mt-5 text-[15px] leading-relaxed text-ink-muted"
        >
          <p className="text-ink font-medium">Твои {term} уже активны. Платить ничего не нужно.</p>
          <p className="mt-2">
            Начни с малого — сфотографируй то, что будешь есть сегодня. Дальше я подскажу.
          </p>
          <p className="mt-3 text-ink-dim">— Дофи</p>
        </motion.div>

        <motion.label
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.55 }}
          className="flex items-start gap-3 mt-6 cursor-pointer select-none"
        >
          <input
            type="checkbox"
            checked={tellGiver}
            onChange={(e) => setTellGiver(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-emerald-glow shrink-0"
          />
          <span className="text-xs text-ink-dim leading-relaxed">
            Дать знать {payload.giver_name}, когда я дойду до финиша «Пути». Больше ничего
            о тебе он не увидит.
          </span>
        </motion.label>

        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          onClick={start}
          className="w-full mt-6 py-4 rounded-2xl bg-gradient-to-r from-emerald-glow to-sky-glow text-bg font-display font-bold text-base shadow-lg active:scale-[0.98] transition-all"
        >
          Начать
        </motion.button>
      </div>
    </div>
  );
}
